import { PiazzaInputContext } from 'ComponentLibrary/src/molecules/Form/PiazzaInputContext'
import { useContext } from 'react'
import { FormDropdown } from 'ComponentLibrary/src/piazza/forms'
import { useForm } from 'react-hook-form'
import { FormSelectField, FormTemplate, FormTextField, InfoBox, tokens, validators } from 'ComponentLibrary'
import { omit, some } from 'lodash'
import { Box, GridLegacy as Grid } from '@mui/material'
import { Contact } from 'ComponentLibrary/src/common/users'
import { InfoIcon } from '@phosphor-icons/react'
import { Typography } from 'ComponentLibrary/src/piazza/text'

type ContactFormProps = {
    setFormIsOpen: (isOpen: boolean) => void
    onSubmitCallback: (data: Contact) => void
    contact?: Contact
    copy?: Partial<ContactFormCopy>
}

export type ContactFormCopy = {
    salutation: string
    salutationOptions: {
        mr: string
        ms: string
        dr: string
        mrDr: string
        msDr: string
        placeholder: string
    }
    name: string
    role: string
    company: string
    street: string
    houseNumber: string
    zip: string
    city: string
    email: string
    invalidEmail: string
    phoneNumber: string
    invalidPhoneNumber: string
    note: string
    infoBox: string
}

export const DEFAULT_CONTACT_FORM_COPY: ContactFormCopy = {
    salutation: 'Anrede',
    salutationOptions: {
        mr: 'Herr',
        ms: 'Frau',
        dr: 'Dr.',
        mrDr: 'Herr Dr.',
        msDr: 'Frau Dr.',
        placeholder: 'Anrede auswählen',
    },
    name: 'Vorname und Nachname',
    role: 'Rolle',
    company: 'Firma',
    street: 'Straße',
    houseNumber: 'Hausnummer',
    zip: 'Postleitzahl',
    city: 'Stadt',
    email: 'E-Mail',
    invalidEmail: 'Keine gültige E-Mail',
    phoneNumber: 'Telefonnummer',
    invalidPhoneNumber: 'Keine gültige Telefonnummer',
    note: 'Anmerkung / Wann soll diese Person kontaktiert werden?',
    infoBox: 'Alle Felder sind optional, bitte geben Sie nur die persönlichen Informationen an welche für eine Kontakaufnahme nötig sind.',
}

export function ContactForm({ setFormIsOpen, onSubmitCallback, contact, copy }: ContactFormProps) {
    const usePiazzaInputs = useContext(PiazzaInputContext)
    const resolvedCopy = { ...DEFAULT_CONTACT_FORM_COPY, ...copy }
    const salutationOptions = { ...DEFAULT_CONTACT_FORM_COPY.salutationOptions, ...copy?.salutationOptions }
    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
    } = useForm<Contact>({
        defaultValues: {
            salutation: contact?.salutation || undefined,
            name: contact?.name,
            job_title: contact?.job_title,
            company: contact?.company,
            street: contact?.street,
            house_number: contact?.house_number,
            country: contact?.country,
            zip: contact?.zip,
            city: contact?.city,
            email: contact?.email,
            phone_number: contact?.phone_number,
            note: contact?.note,
        },
    })

    const onSubmit = handleSubmit((data) => {
        //TODO: have universal empty string -> undefined conversion on optional text fields
        if (data.phone_number === '') {
            data.phone_number = undefined
        }
        if (data.zip === '') {
            data.zip = undefined
        }
        if (data.email === '') {
            data.email = undefined
        }
        isAnyFieldDefined(data) && onSubmitCallback(data)
        setFormIsOpen(false)
    })

    const onCancel = () => {
        reset()
        setFormIsOpen(false)
    }

    return (
        <Box marginBottom={tokens.SizingSpacing300}>
            <FormTemplate onSubmit={onSubmit} onCancel={onCancel} isFormDirty={isDirty} dense>
                {usePiazzaInputs ? (
                    <Grid item xs={3}>
                        <FormDropdown
                            control={control}
                            name="salutation"
                            label={resolvedCopy.salutation}
                            options={[
                                { label: salutationOptions.mr, value: 'Herr' },
                                { label: salutationOptions.ms, value: 'Frau' },
                                { label: salutationOptions.dr, value: 'Dr.' },
                                { label: salutationOptions.mrDr, value: 'Herr Dr.' },
                                { label: salutationOptions.msDr, value: 'Frau Dr.' },
                                { label: salutationOptions.placeholder, value: '' },
                            ]}
                        />
                    </Grid>
                ) : (
                    <FormSelectField
                        label={resolvedCopy.salutation}
                        errors={errors}
                        control={control}
                        fieldName={'salutation'}
                        options={[
                            { label: salutationOptions.mr, value: 'Herr' },
                            { label: salutationOptions.ms, value: 'Frau' },
                            { label: salutationOptions.dr, value: 'Dr.' },
                            { label: salutationOptions.mrDr, value: 'Herr Dr.' },
                            { label: salutationOptions.msDr, value: 'Frau Dr.' },
                            { label: salutationOptions.placeholder, value: '' },
                        ]}
                        xs={3}
                    />
                )}
                <FormTextField control={control} fieldName={'name'} errors={errors} xs={9} label={resolvedCopy.name} />
                <FormTextField control={control} fieldName={'job_title'} errors={errors} label={resolvedCopy.role} xs={12} />
                <FormTextField control={control} fieldName={'company'} errors={errors} label={resolvedCopy.company} xs={12} />
                <FormTextField control={control} fieldName={'street'} errors={errors} label={resolvedCopy.street} xs={8} />
                <FormTextField control={control} fieldName={'house_number'} errors={errors} label={resolvedCopy.houseNumber} xs={4} />
                <FormTextField control={control} fieldName={'zip'} errors={errors} label={resolvedCopy.zip} xs={4} />
                <FormTextField control={control} fieldName={'city'} errors={errors} label={resolvedCopy.city} xs={8} />
                <FormTextField
                    control={control}
                    fieldName={'email'}
                    errors={errors}
                    label={resolvedCopy.email}
                    xs={6}
                    rules={{ validate: (value) => validators.isEmail(value) || resolvedCopy.invalidEmail }}
                />
                <FormTextField
                    control={control}
                    fieldName={'phone_number'}
                    errors={errors}
                    label={resolvedCopy.phoneNumber}
                    xs={6}
                    rules={{ validate: (value) => validators.isMobilePhone(value) || resolvedCopy.invalidPhoneNumber }}
                />
                <FormTextField control={control} fieldName={'note'} errors={errors} multiline xs={12} label={resolvedCopy.note} />
            </FormTemplate>
        </Box>
    )
}

const isAnyFieldDefined = (contact: Contact): boolean => {
    const filteredContact = omit(contact, ['salutation'])
    return some(filteredContact, (value) => value !== undefined)
}

export function ContactFormInfoBox({ text = DEFAULT_CONTACT_FORM_COPY.infoBox }: { text?: string }) {
    return (
        <InfoBox variant="ordinary" icon={<InfoIcon />}>
            <Typography variant="textS" color={tokens.ColorTextBold}>
                {text}
            </Typography>
        </InfoBox>
    )
}
